import { useState } from "react";
import { Button } from "@/components/ui/button";
import { Label } from "@/components/ui/label";
import { Popover, PopoverContent, PopoverTrigger } from "@/components/ui/popover";
import { Command, CommandEmpty, CommandGroup, CommandInput, CommandItem, CommandList } from "@/components/ui/command";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { Check, ChevronsUpDown } from "lucide-react";

// Item type → the stage / store its stock lives in. Mirrors the backend's
// `default_store_for_type`, which decides where a Job Work GIN's received
// part is credited.
const TYPE_STAGE = {
  RAW: { stage: "Raw Material", store: "RM_STORE", storeName: "Raw Material Store" },
  MS: { stage: "Machine Shop", store: "MS_STORE", storeName: "Machine Shop Store" },
  IMP: { stage: "Impregnation", store: "IMP_STORE", storeName: "Impregnation Store" },
  FAB: { stage: "Fabrication", store: "FAB_STORE", storeName: "Fabrication Store" },
  PWD: { stage: "Powder Coating", store: "PWD_STORE", storeName: "Powder Coating Store" },
  SFG: { stage: "Semi Finished", store: "SFG_STORE", storeName: "Semi Finished Goods Store" },
  ASM: { stage: "Assembly", store: "ASM_STORE", storeName: "Assembly Store" },
  TEST: { stage: "Testing", store: "TEST_STORE", storeName: "Testing Store" },
  FI: { stage: "Final Inspection", store: "FI_STORE", storeName: "Final Inspection Store" },
  FG: { stage: "Finished Goods", store: "FG_STORE", storeName: "Finished Goods Store" },
  RWK: { stage: "Rework", store: "REWORK_STORE", storeName: "Rework Store" },
};

export const receivedPartStage = (item) => TYPE_STAGE[item?.type] || TYPE_STAGE.RAW;

// Every store a Target Store override can point at (backend STORES).
const STORE_OPTIONS = [
  ...Object.values(TYPE_STAGE).map((t) => ({ code: t.store, name: t.storeName })),
  { code: "REJECTED_STORE", name: "Rejected Warehouse" },
];
const AUTO = "__auto__";

export const RECEIVED_PART_HELP =
  "Select the part number at which this material is returning — e.g. if sent as Raw Material and returning after machining, select the MC part number";

// "Received Part Number (stage after job work)" on a GIN raised against a Job
// Work DC — a type-to-search list of every part number across all stages —
// plus the line's Target Store: defaults to the received part's stage store,
// overridable when `onTargetStore` is given ("" = that default).
export default function ReceivedPartPicker({ value, items, onPick, storeMap = {}, testId, showHelp = true,
                                             targetStore = "", onTargetStore }) {
  const [open, setOpen] = useState(false);
  const selected = items.find((it) => it.id === value);
  const dest = selected ? receivedPartStage(selected) : null;
  const storeName = (code) => storeMap[code] || STORE_OPTIONS.find((o) => o.code === code)?.name || code;
  const destStore = targetStore || dest?.store;
  return (
    <div>
      <Label className="text-[10px] uppercase">Received Part Number (stage after job work) *</Label>
      <Popover open={open} onOpenChange={setOpen} modal>
        <PopoverTrigger asChild>
          <Button variant="outline" role="combobox"
            className={`w-full justify-between h-9 font-normal ${selected ? "" : "border-amber-400 text-slate-500"}`}
            data-testid={testId}>
            <span className="truncate text-left">{selected ? `${selected.code} — ${selected.name}` : "Select received part number…"}</span>
            <ChevronsUpDown className="w-3.5 h-3.5 opacity-50 shrink-0 ml-1" />
          </Button>
        </PopoverTrigger>
        <PopoverContent className="p-0 w-[440px]" align="start">
          <Command>
            <CommandInput placeholder="Search by part number, name or stage…" data-testid={`${testId}-search`} />
            <CommandList>
              <CommandEmpty>No part numbers found</CommandEmpty>
              <CommandGroup>
                {items.map((it) => (
                  <CommandItem
                    key={it.id}
                    value={`${it.code} ${it.name} ${receivedPartStage(it).stage} ${it.id}`}
                    onSelect={() => { onPick(it.id); setOpen(false); }}
                    data-testid={`${testId}-opt-${it.code}`}
                  >
                    <Check className={`w-3.5 h-3.5 mr-2 ${value === it.id ? "opacity-100" : "opacity-0"}`} />
                    <div className="flex flex-col min-w-0">
                      <span className="text-sm font-medium">{it.code}</span>
                      <span className="text-[11px] text-slate-500 truncate">{it.name} · {receivedPartStage(it).stage}</span>
                    </div>
                  </CommandItem>
                ))}
              </CommandGroup>
            </CommandList>
          </Command>
        </PopoverContent>
      </Popover>
      {showHelp && <p className="text-[11px] text-slate-500 mt-1">{RECEIVED_PART_HELP}</p>}
      {onTargetStore && (
        <div className="mt-2">
          <Label className="text-[10px] uppercase">Target Store</Label>
          <Select value={targetStore || AUTO} onValueChange={(v) => onTargetStore(v === AUTO ? "" : v)}>
            <SelectTrigger className="h-9" data-testid={`${testId}-store`}><SelectValue /></SelectTrigger>
            <SelectContent>
              <SelectItem value={AUTO}>Auto — {dest ? `${storeName(dest.store)} (received part's stage)` : "received part's stage store"}</SelectItem>
              {STORE_OPTIONS.map((o) => <SelectItem key={o.code} value={o.code}>{storeName(o.code)}</SelectItem>)}
            </SelectContent>
          </Select>
        </div>
      )}
      {dest && (
        <div className="text-[11px] text-slate-600 mt-1" data-testid={`${testId}-dest`}>
          Stage: <strong>{dest.stage}</strong> · QC-accepted qty goes to <strong>{storeName(destStore)}</strong>; rejected qty to Rework Store
        </div>
      )}
    </div>
  );
}
